import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useAnchoredPosition from "../../../hooks/useAnchoredPosition";

// =====================================================================
// N2-1 filter dropdown button (Redesign 2.0 §1.9 "Filter dropdown button").
//
// The artboard trigger is a composite the platform CustomDropdown trigger
// cannot render ("Position" 12.5/600 ink + value "All" in #9CA3AF/400 +
// U+25BE caret 9px) — per brief Rule 1 the artboard wins, so this is a thin
// presentational trigger around the SAME portal/anchor mechanics every
// platform dropdown uses (hooks/useAnchoredPosition, D-003) and a plain
// option list. No search mechanic here (Rule 11 concerns search inputs).
//
// variant "button"  — desktop N2-1 toolbar: white, border #E5E7EB, r8,
//                     padding 8px 12px, hover #F3F4F6.
// variant "pill"    — M1 mobile filter row: r999, padding 6px 12px, 12px,
//                     label + caret only ("Position ▾").
// =====================================================================

const CnFilterDropdown = ({
  label,
  options = [],
  value = "",
  onChange,
  allLabel = "All",
  variant = "button",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const { panelRef, style: menuStyle } = useAnchoredPosition(open, triggerRef, {
    align: "left",
    gap: 4,
  });

  useEffect(() => {
    const onDown = (e) => {
      const inTrigger = wrapRef.current && wrapRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [panelRef]);

  const selected = options.find((o) => o.value === value);
  const valueLabel = value && selected ? selected.label : allLabel;
  const pill = variant === "pill";

  return (
    <div className="relative flex-none" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={
          pill
            ? `inline-flex items-center gap-1.5 bg-white border border-[#E5E7EB] rounded-full px-3 py-1.5 text-[12px] font-semibold text-[#111827] whitespace-nowrap ${className}`
            : `inline-flex items-center gap-1.5 bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-[12.5px] font-semibold text-[#111827] hover:bg-[#F3F4F6] whitespace-nowrap ${className}`
        }
      >
        {label}
        {/* Desktop trigger carries the current value ("All" when unset). */}
        {!pill && <span className="text-[#9CA3AF] font-normal">{valueLabel}</span>}
        {/* U+25BE caret, verbatim (brief Rule 2). */}
        <span className="text-[#9CA3AF] text-[9px] leading-none" aria-hidden="true">
          ▾
        </span>
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={menuStyle || { position: "fixed", visibility: "hidden" }}
            className="z-[70] min-w-[180px] max-h-72 overflow-y-auto bg-white border border-[#E2E8F0] rounded-[9px] shadow-[0_12px_32px_rgba(15,23,42,0.14)] py-1"
            role="listbox"
          >
            {options.map((o) => {
              const active = o.value === value;
              return (
                <button
                  key={o.value || "__all"}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-[12.5px] font-medium ${
                    active
                      ? "text-[#2283FF] bg-[#EEF4FE]"
                      : "text-[#334155] hover:bg-[#F3F4F6]"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CnFilterDropdown;
